import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Globe,
  User,
  Briefcase,
  FolderKanban,
  FileText,
  Edit2,
  Archive,
  Plus,
  ExternalLink,
  History,
  Save,
  TrendingUp,
  CheckCircle2,
  Clock,
  Map,
} from 'lucide-react';
import {
  useCustomer,
  useCustomerOverview,
  useUpdateCustomer,
  useCustomerAdoptions,
  useUpdateCustomerAdoptions,
  useCreateAdoptionVersion,
  useAdoptionVersions,
  Customer,
  CustomerAdoption,
  RagStatus,
} from '@/hooks/useCustomers';
import { useHasPermission } from '@/hooks/useAuth';
import {
  useOpportunities,
  useCreateOpportunity,
  useCreateProjectUnderOpportunity,
} from '@/hooks/useOpportunities';
import { useProjects } from '@/hooks/useProjects';
import { useRateCardTypes } from '@/hooks/useRateCards';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { UserPicker } from '@/components/common/UserPicker';
import { cn } from '@/lib/utils';

type TabId = 'overview' | 'adoption' | 'projects' | 'opportunities' | 'notes';

const TABS: Array<{ id: TabId; label: string; icon: typeof Building2 }> = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'adoption', label: 'Adoption Plan', icon: FileText },
  { id: 'projects', label: 'Projects', icon: Briefcase },
  { id: 'opportunities', label: 'Opportunities', icon: FolderKanban },
  { id: 'notes', label: 'Notes', icon: FileText },
];

const STATUS_COLORS: Record<Customer['status'], string> = {
  prospect: 'bg-yellow-100 text-yellow-700',
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<Customer['status'], string> = {
  prospect: 'Prospect',
  active: 'Active',
  inactive: 'Inactive',
};

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canEdit = useHasPermission('customers.update');
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateOpportunityModal, setShowCreateOpportunityModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

  const { data: customerData, isLoading, refetch } = useCustomer(id!);
  const { data: projectsData } = useProjects({ customerId: id, limit: 50 });
  const { data: opportunitiesData } = useOpportunities({ customerId: id, limit: 50 });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!customerData?.customer) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Customer not found
      </div>
    );
  }

  const customer = customerData.customer;
  const projects = projectsData?.projects || [];
  const opportunities = opportunitiesData?.opportunities || [];

  const handleArchive = () => {
    if (window.confirm('Are you sure you want to archive this customer?')) {
      navigate('/customers');
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 pb-4 mb-4 md:mb-6">
        <div className="flex items-start md:items-center gap-3 md:gap-4">
          <button
            onClick={() => navigate('/customers')}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 transition-colors touch-target flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-start md:items-center gap-3 md:gap-4 flex-1 min-w-0">
            <div className="rounded-xl bg-gradient-to-br from-xtravirt-cyan to-xtravirt-navy p-2.5 md:p-3 flex-shrink-0">
              <Building2 className="h-6 w-6 md:h-8 md:w-8 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-lg md:text-2xl font-bold text-xtravirt-dark truncate">
                  {customer.name}
                </h1>
                <span
                  className={cn(
                    'rounded-full px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm font-medium flex-shrink-0',
                    STATUS_COLORS[customer.status]
                  )}
                >
                  {STATUS_LABELS[customer.status]}
                </span>
              </div>
              {customer.industry && (
                <p className="text-sm text-gray-600 mt-1 truncate">{customer.industry}</p>
              )}
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2 ml-11 md:ml-0">
            <button
              onClick={() => setShowEditModal(true)}
              className="btn-secondary text-sm"
            >
              <Edit2 className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Edit</span>
            </button>
            <button
              onClick={handleArchive}
              className="btn-secondary text-red-600 hover:bg-red-50 text-sm"
            >
              <Archive className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Archive</span>
            </button>
          </div>
        )}
      </div>

      {/* Tabs - horizontal scroll on mobile */}
      <div className="border-b border-gray-200 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <nav className="flex gap-4 md:gap-6 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-1.5 md:gap-2 border-b-2 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap touch-target',
                activeTab === tab.id
                  ? 'border-xtravirt-cyan text-xtravirt-cyan'
                  : 'border-transparent text-gray-600 hover:text-xtravirt-dark'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.id === 'projects' && projects.length > 0 && (
                <span className="ml-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs">
                  {projects.length}
                </span>
              )}
              {tab.id === 'opportunities' && opportunities.length > 0 && (
                <span className="ml-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs">
                  {opportunities.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto py-4 md:py-6">
        {activeTab === 'overview' && (
          <OverviewTab
            customer={customer}
            customerId={id!}
            onCreateOpportunity={() => setShowCreateOpportunityModal(true)}
            onCreateProject={() => setShowCreateProjectModal(true)}
          />
        )}

        {activeTab === 'adoption' && (
          <AdoptionPlanTab customerId={id!} />
        )}

        {activeTab === 'projects' && (
          <ProjectsTab
            projects={projects}
            customerId={id!}
            onCreateProject={() => setShowCreateProjectModal(true)}
          />
        )}

        {activeTab === 'opportunities' && (
          <OpportunitiesTab
            opportunities={opportunities}
            customerId={id!}
            onCreateOpportunity={() => setShowCreateOpportunityModal(true)}
          />
        )}

        {activeTab === 'notes' && (
          <NotesTab customer={customer} onRefresh={refetch} />
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditCustomerModal
          customer={customer}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            refetch();
            setShowEditModal(false);
          }}
        />
      )}

      {/* Create Opportunity Modal */}
      {showCreateOpportunityModal && (
        <CreateOpportunityForCustomerModal
          customerId={customer.id}
          customerName={customer.name}
          onClose={() => setShowCreateOpportunityModal(false)}
        />
      )}

      {/* Create Project Modal */}
      {showCreateProjectModal && (
        <CreateProjectForCustomerModal
          customerId={customer.id}
          customerName={customer.name}
          opportunities={opportunities}
          onClose={() => setShowCreateProjectModal(false)}
        />
      )}
    </div>
  );
}

// ============ Overview Tab ============

interface OverviewTabProps {
  customer: Customer;
  customerId: string;
  onCreateOpportunity: () => void;
  onCreateProject: () => void;
}

const RAG_LABELS: Record<RagStatus, string> = {
  green: 'On Track',
  amber: 'At Risk',
  red: 'Blocked',
  na: 'Not Set',
};

const RAG_DOT: Record<RagStatus, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  na: 'bg-gray-300',
};

function OverviewTab({
  customer,
  customerId,
  onCreateOpportunity,
  onCreateProject,
}: OverviewTabProps) {
  const { data: overview, isLoading } = useCustomerOverview(customerId);

  if (isLoading || !overview) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  const { pipeline, health, adoption, tarPlan, activeOpportunities, activeProjects } = overview;

  const scopedPct = pipeline.totalValue > 0 ? Math.round((pipeline.scopedValue / pipeline.totalValue) * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
      {/* Main Column */}
      <div className="lg:col-span-2 space-y-4 md:space-y-6">
        {/* Pipeline Value */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-xtravirt-cyan" />
            <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
              Pipeline Value
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Total Pipeline</span>
              <span className="font-semibold text-xtravirt-dark text-base">
                {formatCurrency(pipeline.pipelineValue)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-gray-500">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                Scoped
              </span>
              <span className="font-semibold text-green-700">
                {formatCurrency(pipeline.scopedValue)}
                <span className="text-xs text-gray-500 ml-1">({scopedPct}%)</span>
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1 text-gray-500">
                <Clock className="h-3.5 w-3.5 text-amber-600" />
                Unscoped
              </span>
              <span className="font-semibold text-amber-600">
                {formatCurrency(pipeline.unscopedValue)}
              </span>
            </div>
            <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mt-3">
              <div
                className="absolute left-0 top-0 h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${scopedPct}%` }}
              />
            </div>
            <div className="pt-2 mt-2 border-t border-gray-100 flex items-center justify-between text-sm">
              <span className="text-gray-500">In Delivery</span>
              <span className="font-semibold text-xtravirt-dark">
                {formatCurrency(pipeline.deliveredValue)}
                <span className="text-xs text-gray-500 ml-1">({pipeline.deliveryOpportunityCount} opp)</span>
              </span>
            </div>
          </div>
        </div>

        {/* Active Opportunities preview */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
              Active Opportunities
            </h3>
            <button
              onClick={onCreateOpportunity}
              className="btn-secondary text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              New
            </button>
          </div>
          {activeOpportunities.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No active opportunities
            </p>
          ) : (
            <div className="space-y-2">
              {activeOpportunities.map((opp) => (
                <Link
                  key={opp.id}
                  to={`/opportunities/${opp.id}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 hover:border-xtravirt-cyan hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={cn('h-2 w-2 rounded-full flex-shrink-0', RAG_DOT[opp.customerRelationshipStatus])} />
                      <span className="font-medium text-xtravirt-dark truncate">{opp.title}</span>
                    </div>
                    {opp.currentScopingStage && (
                      <p className="text-xs text-gray-500 mt-0.5 capitalize">
                        {opp.currentScopingStage.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {opp.servicesValue > 0 && (
                      <p className="text-sm font-semibold text-green-700">
                        {formatCurrency(opp.servicesValue, opp.currency as 'GBP' | 'USD' | 'EUR')}
                      </p>
                    )}
                    {opp.scopedValue > 0 && (
                      <p className="text-xs text-gray-500">
                        {formatCurrency(opp.scopedValue, opp.currency as 'GBP' | 'USD' | 'EUR')} scoped
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Active Projects preview */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
              Active Projects
            </h3>
            <button
              onClick={onCreateProject}
              className="btn-secondary text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              New
            </button>
          </div>
          {activeProjects.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No active projects
            </p>
          ) : (
            <div className="space-y-2">
              {activeProjects.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 hover:border-xtravirt-cyan hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={cn('h-2 w-2 rounded-full flex-shrink-0', RAG_DOT[p.healthStatus])} />
                      <span className="font-medium text-xtravirt-dark truncate">{p.name}</span>
                    </div>
                    {p.opportunityTitle && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {p.opportunityTitle}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {p.totalCost > 0 && (
                      <p className="text-sm font-semibold text-green-700">
                        {formatCurrency(p.totalCost, p.currency as 'GBP' | 'USD' | 'EUR')}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {(p.totalAdjustedDays / 100).toFixed(0)} days
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Description */}
        {customer.description && (
          <div className="card p-4 md:p-6">
            <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-2 md:mb-3">
              Description
            </h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {customer.description}
            </p>
          </div>
        )}
      </div>

      {/* Side Column */}
      <div className="space-y-4 md:space-y-6">
        {/* Health Status */}
        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">
            Health
          </h3>
          <div className="space-y-3">
            <HealthRow label="Customer Relationship" status={health.customerRelationship} />
            <HealthRow label="Scoping" status={health.scoping} />
            <HealthRow label="Readiness" status={health.readiness} />
            <HealthRow label="Delivery" status={health.delivery} />
            <HealthRow label="Active Projects" status={health.projects} />
            <div className="pt-3 border-t border-gray-100">
              <HealthRow label="Overall" status={health.overall} bold />
            </div>
          </div>
        </div>

        {/* TAR Plan */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Map className="h-5 w-5 text-xtravirt-cyan" />
            <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
              TAR Plan
            </h3>
          </div>
          {tarPlan ? (
            <div>
              <p className="text-sm font-medium text-xtravirt-dark">{tarPlan.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Updated {new Date(tarPlan.updatedAt).toLocaleDateString()}
              </p>
              <Link
                to={`/tar/${tarPlan.id}`}
                className="mt-2 inline-flex items-center gap-1 text-xs text-xtravirt-cyan hover:underline"
              >
                View TAR Plan →
              </Link>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No TAR plan yet</p>
          )}
        </div>

        {/* Adoption Snapshot */}
        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3">
            Adoption
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Score</span>
              <span className="font-semibold text-xtravirt-cyan">
                {adoption.scorePercent}%
              </span>
            </div>
            <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-xtravirt-cyan rounded-full"
                style={{ width: `${adoption.scorePercent}%` }}
              />
            </div>
            <div className="pt-2 space-y-1 text-xs text-gray-600">
              <div className="flex justify-between">
                <span>In Use</span>
                <span className="font-medium">{adoption.counts.inUse}</span>
              </div>
              <div className="flex justify-between">
                <span>Certified</span>
                <span className="font-medium">{adoption.counts.certified}</span>
              </div>
              <div className="flex justify-between">
                <span>PoC</span>
                <span className="font-medium">{adoption.counts.poc}</span>
              </div>
              <div className="flex justify-between">
                <span>Discussion</span>
                <span className="font-medium">{adoption.counts.discussion}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Not Yet</span>
                <span>{adoption.counts.notYet}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3 md:mb-4">
            Company Info
          </h3>
          <dl className="space-y-3">
            <DetailItem
              icon={Building2}
              label="Industry"
              value={customer.industry || '-'}
            />
            <DetailItem
              icon={MapPin}
              label="Region"
              value={customer.region || '-'}
            />
            <DetailItem
              icon={MapPin}
              label="Country"
              value={customer.country || '-'}
            />
            <DetailItem
              icon={Globe}
              label="Website"
              value={customer.website || '-'}
              isLink={!!customer.website}
            />
            <DetailItem
              icon={User}
              label="Account Manager"
              value={customer.accountManager || '-'}
            />
            <DetailItem
              icon={User}
              label="Technical Lead"
              value={customer.technicalLead || '-'}
            />
          </dl>
        </div>
      </div>
    </div>
  );
}

// ============ Health Row ============

interface HealthRowProps {
  label: string;
  status: RagStatus;
  bold?: boolean;
}

function HealthRow({ label, status, bold }: HealthRowProps) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={cn('text-gray-600', bold && 'font-semibold text-gray-900')}>{label}</span>
      <div className="flex items-center gap-1.5">
        <span className={cn('h-2.5 w-2.5 rounded-full', RAG_DOT[status])} />
        <span className={cn('text-xs', bold && 'font-semibold')}>{RAG_LABELS[status]}</span>
      </div>
    </div>
  );
}

// ============ Detail Item ============

interface DetailItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  isLink?: boolean;
}

function DetailItem({ icon: Icon, label, value, isLink }: DetailItemProps) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div>
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="text-sm font-medium text-gray-900 mt-0.5">
          {isLink && value !== '-' ? (
            <a
              href={value.startsWith('http') ? value : `https://${value}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xtravirt-cyan hover:underline flex items-center gap-1"
            >
              {value}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            value
          )}
        </dd>
      </div>
    </div>
  );
}

// ============ Adoption Plan Tab ============

const ADOPTION_STATUSES = [
  { id: 'not_yet', label: 'Not Yet', color: 'bg-gray-100 text-gray-600' },
  { id: 'discussion', label: 'Discussion', color: 'bg-blue-100 text-blue-700' },
  { id: 'poc', label: 'PoC', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'certified', label: 'Certified', color: 'bg-purple-100 text-purple-700' },
  { id: 'in_use', label: 'In Use', color: 'bg-green-100 text-green-700' },
  { id: 'deferred', label: 'Deferred', color: 'bg-orange-100 text-orange-700' },
  { id: 'decomm', label: 'Decomm', color: 'bg-red-100 text-red-700' },
] as const;

const VCF_COMPONENTS = [
  { id: 'vsphere', name: 'vSphere 8' },
  { id: 'vsan', name: 'vSAN' },
  { id: 'nsx', name: 'NSX' },
  { id: 'aria_ops', name: 'Aria Operations' },
  { id: 'aria_auto', name: 'Aria Automation' },
  { id: 'tanzu', name: 'Tanzu' },
  { id: 'hcx', name: 'HCX' },
  { id: 'srm', name: 'SRM' },
];

const YEARS = ['Y1', 'Y2', 'Y3', 'Y4', 'Y5'];

interface AdoptionPlanTabProps {
  customerId: string;
}

function AdoptionPlanTab({ customerId }: AdoptionPlanTabProps) {
  const { data: adoptionsData, isLoading, refetch } = useCustomerAdoptions(customerId);
  const { data: versionsData } = useAdoptionVersions(customerId);
  const updateAdoptions = useUpdateCustomerAdoptions();
  const createVersion = useCreateAdoptionVersion();

  const [localAdoptions, setLocalAdoptions] = useState<Record<string, CustomerAdoption['adoptionStatus']>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Build adoption grid data
  const adoptionGrid = useMemo(() => {
    const grid: Record<string, Record<number, CustomerAdoption['adoptionStatus']>> = {};

    // Initialize with existing data
    for (const adoption of adoptionsData?.adoptions || []) {
      const key = adoption.componentId;
      if (!grid[key]) grid[key] = {};
      grid[key][adoption.year] = adoption.adoptionStatus;
    }

    // Merge with local changes
    for (const [key, status] of Object.entries(localAdoptions)) {
      const [componentId, year] = key.split('-');
      if (!grid[componentId]) grid[componentId] = {};
      grid[componentId][parseInt(year)] = status;
    }

    return grid;
  }, [adoptionsData, localAdoptions]);

  const handleStatusChange = (componentId: string, year: number, status: CustomerAdoption['adoptionStatus']) => {
    const key = `${componentId}-${year}`;
    setLocalAdoptions(prev => ({ ...prev, [key]: status }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    const adoptions: Array<{
      componentId: string;
      year: number;
      adoptionStatus: CustomerAdoption['adoptionStatus'];
    }> = [];

    for (const [key, status] of Object.entries(localAdoptions)) {
      const [componentId, year] = key.split('-');
      adoptions.push({
        componentId,
        year: parseInt(year),
        adoptionStatus: status,
      });
    }

    await updateAdoptions.mutateAsync({
      customerId,
      adoptions,
    });

    setHasChanges(false);
    setLocalAdoptions({});
    refetch();
  };

  const handleCreateVersion = async () => {
    await createVersion.mutateAsync({ customerId });
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
            Adoption Plan
          </h3>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Track VCF component adoption journey over 5 years
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCreateVersion}
            disabled={createVersion.isPending}
            className="btn-secondary text-sm"
          >
            <History className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{createVersion.isPending ? 'Creating...' : 'Create Version'}</span>
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateAdoptions.isPending}
            className={cn(
              'btn-primary text-sm',
              !hasChanges && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Save className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{updateAdoptions.isPending ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      {/* Adoption Grid */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  VCF Component
                </th>
                {YEARS.map((year) => (
                  <th
                    key={year}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32"
                  >
                    {year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {VCF_COMPONENTS.map((component) => (
                <tr key={component.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {component.name}
                  </td>
                  {YEARS.map((_, yearIdx) => {
                    const year = yearIdx + 1;
                    const currentStatus = adoptionGrid[component.id]?.[year] || 'not_yet';

                    return (
                      <td key={year} className="px-4 py-3 text-center">
                        <select
                          value={currentStatus}
                          onChange={(e) =>
                            handleStatusChange(
                              component.id,
                              year,
                              e.target.value as CustomerAdoption['adoptionStatus']
                            )
                          }
                          className={cn(
                            'text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer',
                            ADOPTION_STATUSES.find((s) => s.id === currentStatus)?.color
                          )}
                        >
                          {ADOPTION_STATUSES.map((status) => (
                            <option key={status.id} value={status.id}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs">
        <span className="text-gray-500 font-medium w-full sm:w-auto">Legend:</span>
        {ADOPTION_STATUSES.map((status) => (
          <span
            key={status.id}
            className={cn('px-2 py-0.5 md:py-1 rounded-full', status.color)}
          >
            {status.label}
          </span>
        ))}
      </div>

      {/* Version History */}
      {versionsData?.versions && versionsData.versions.length > 0 && (
        <div className="card p-4">
          <h4 className="font-medium text-xtravirt-dark mb-3">Version History</h4>
          <div className="space-y-2 text-sm">
            {versionsData.versions.slice(0, 5).map((version) => (
              <div
                key={version.id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <span className="font-medium">v{version.version}</span>
                  {version.version === versionsData.versions[0].version && (
                    <span className="ml-2 text-green-600 text-xs">Current</span>
                  )}
                </div>
                <span className="text-gray-500">
                  {new Date(version.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Projects Tab ============

interface ProjectsTabProps {
  projects: Array<{
    id: string;
    name: string;
    status: string;
    totalAdjustedDays: number;
    totalCost: number;
    currency: string | null;
    updatedAt: string;
  }>;
  customerId: string;
  onCreateProject: () => void;
}

function ProjectsTab({ projects, customerId: _customerId, onCreateProject }: ProjectsTabProps) {
  const navigate = useNavigate();

  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        <Briefcase className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
        <p className="text-sm text-gray-500 mb-4">
          Create a project to start scoping VCF services
        </p>
        <button onClick={onCreateProject} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Create Project
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-xtravirt-dark">
          Projects ({projects.length})
        </h3>
        <button onClick={onCreateProject} className="btn-secondary text-sm">
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((project) => (
          <div
            key={project.id}
            onClick={() => navigate(`/projects/${project.id}`)}
            className="card p-4 cursor-pointer hover:border-xtravirt-cyan/50 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium text-xtravirt-dark">{project.name}</h4>
                <span
                  className={cn(
                    'inline-block mt-1 text-xs px-2 py-0.5 rounded-full',
                    project.status === 'approved'
                      ? 'bg-green-100 text-green-700'
                      : project.status === 'draft'
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-yellow-100 text-yellow-700'
                  )}
                >
                  {project.status}
                </span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {(project.totalAdjustedDays / 100).toFixed(1)} days
              </span>
              <span className="font-medium text-green-700">
                {formatCurrency(project.totalCost, project.currency as 'GBP' | 'USD' | 'EUR')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ Opportunities Tab ============

interface OpportunitiesTabProps {
  opportunities: Array<{
    id: string;
    title: string;
    engagementStatus: string;
    currentScopingStage: string | null;
    servicesValue: number | null;
    currency: 'GBP' | 'USD' | 'EUR';
    funding: string | null;
    fundingTypeId: string | null;
    fundingTypeName: string | null;
  }>;
  customerId: string;
  onCreateOpportunity: () => void;
}

function OpportunitiesTab({ opportunities, customerId: _customerId, onCreateOpportunity }: OpportunitiesTabProps) {
  const navigate = useNavigate();

  if (opportunities.length === 0) {
    return (
      <div className="text-center py-12">
        <FolderKanban className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No opportunities yet</h3>
        <p className="text-sm text-gray-500 mb-4">
          Create an opportunity to track pipeline activity
        </p>
        <button onClick={onCreateOpportunity} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Create Opportunity
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
          Opportunities ({opportunities.length})
        </h3>
        <button onClick={onCreateOpportunity} className="btn-secondary text-sm">
          <Plus className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">New Opportunity</span>
        </button>
      </div>

      {/* Mobile: Card view */}
      <div className="md:hidden space-y-3">
        {opportunities.map((opp) => (
          <div
            key={opp.id}
            onClick={() => navigate(`/opportunities/${opp.id}`)}
            className="card p-4 cursor-pointer hover:border-xtravirt-cyan/50 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-xtravirt-dark">{opp.title}</h4>
              {opp.servicesValue ? (
                <span className="font-medium text-green-700 text-sm flex-shrink-0">
                  {formatCurrency(opp.servicesValue, opp.currency)}
                </span>
              ) : null}
            </div>
            <div className="flex items-center justify-between mt-2 text-sm">
              <span className="text-gray-600">
                {opp.currentScopingStage?.replace(/_/g, ' ') || '-'}
              </span>
              {(opp.fundingTypeName || opp.funding) && (
                <span className="text-gray-500">{opp.fundingTypeName || opp.funding}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: Table view */}
      <div className="hidden md:block card overflow-hidden p-0">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Opportunity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Stage
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Value
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Funding
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {opportunities.map((opp) => (
              <tr
                key={opp.id}
                onClick={() => navigate(`/opportunities/${opp.id}`)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-4 py-3">
                  <span className="font-medium text-xtravirt-dark">{opp.title}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {opp.currentScopingStage?.replace(/_/g, ' ') || '-'}
                </td>
                <td className="px-4 py-3">
                  {opp.servicesValue ? (
                    <span className="font-medium text-green-700">
                      {formatCurrency(opp.servicesValue, opp.currency)}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {(opp.fundingTypeName || opp.funding) ? (
                    <span className="text-sm text-gray-600">{opp.fundingTypeName || opp.funding}</span>
                  ) : (
                    <span className="text-gray-400">TBC</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ Notes Tab ============

interface NotesTabProps {
  customer: Customer;
  onRefresh: () => void;
}

function NotesTab({ customer, onRefresh }: NotesTabProps) {
  const updateCustomer = useUpdateCustomer();
  const [notes, setNotes] = useState(customer.notes || '');
  const [hasChanges, setHasChanges] = useState(false);

  const handleSave = async () => {
    await updateCustomer.mutateAsync({
      id: customer.id,
      notes,
    });
    setHasChanges(false);
    onRefresh();
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-xtravirt-dark">
          Notes
        </h3>
        <button
          onClick={handleSave}
          disabled={!hasChanges || updateCustomer.isPending}
          className={cn(
            'btn-primary',
            !hasChanges && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Save className="h-4 w-4 mr-2" />
          {updateCustomer.isPending ? 'Saving...' : 'Save Notes'}
        </button>
      </div>

      <div className="card p-4">
        <textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setHasChanges(true);
          }}
          placeholder="Add notes about this customer..."
          rows={12}
          className="input w-full"
        />
      </div>
    </div>
  );
}

// ============ Edit Customer Modal ============

interface EditCustomerModalProps {
  customer: Customer;
  onClose: () => void;
  onSuccess: () => void;
}

function EditCustomerModal({ customer, onClose, onSuccess }: EditCustomerModalProps) {
  const updateCustomer = useUpdateCustomer();
  const [formData, setFormData] = useState({
    name: customer.name,
    industry: customer.industry || '',
    region: (customer.region || '') as 'EMEA' | 'Americas' | 'APAC' | '',
    country: customer.country || '',
    website: customer.website || '',
    status: customer.status,
    tier: (customer.tier || 'standard') as 'standard' | 'accelerated' | 'enterprise',
    accountManager: customer.accountManager || '',
    accountManagerId: customer.accountManagerId || null as string | null,
    technicalLead: customer.technicalLead || '',
    technicalLeadId: customer.technicalLeadId || null as string | null,
    description: customer.description || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateCustomer.mutateAsync({
      id: customer.id,
      ...formData,
      region: formData.region || undefined,
    } as Parameters<typeof updateCustomer.mutateAsync>[0]);
    onSuccess();
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-2xl">
        <div className="dialog-header">
          <h2 className="dialog-title">Edit Customer</h2>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Company Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input mt-1 w-full"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Industry</label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="input mt-1 w-full"
              />
            </div>
            <div>
              <label className="label">Region</label>
              <select
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value as '' | 'EMEA' | 'Americas' | 'APAC' })}
                className="input mt-1 w-full"
              >
                <option value="">Select region</option>
                <option value="EMEA">EMEA</option>
                <option value="Americas">Americas</option>
                <option value="APAC">APAC</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="input mt-1 w-full"
              />
            </div>
            <div>
              <label className="label">Website</label>
              <input
                type="text"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="input mt-1 w-full"
                placeholder="www.example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as Customer['status'] })}
                className="input mt-1 w-full"
              >
                <option value="active">Active</option>
                <option value="prospect">Prospect</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="label">Tier</label>
              <select
                value={formData.tier}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value as 'standard' | 'accelerated' | 'enterprise' })}
                className="input mt-1 w-full"
              >
                <option value="standard">Standard</option>
                <option value="accelerated">Accelerated</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Account Manager</label>
              <UserPicker
                userId={formData.accountManagerId}
                userName={formData.accountManager}
                onChange={({ userId, userName }) =>
                  setFormData({
                    ...formData,
                    accountManagerId: userId,
                    accountManager: userName || '',
                  })
                }
                placeholder="Pick or type name…"
                className="mt-1"
              />
            </div>
            <div>
              <label className="label">Technical Lead</label>
              <UserPicker
                userId={formData.technicalLeadId}
                userName={formData.technicalLead}
                onChange={({ userId, userName }) =>
                  setFormData({
                    ...formData,
                    technicalLeadId: userId,
                    technicalLead: userName || '',
                  })
                }
                placeholder="Pick or type name…"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="input mt-1 w-full"
            />
          </div>
        </form>

        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateCustomer.isPending}
            className="btn-primary"
          >
            {updateCustomer.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Create Opportunity Modal ============

interface CreateOpportunityForCustomerModalProps {
  customerId: string;
  customerName: string;
  onClose: () => void;
}

function CreateOpportunityForCustomerModal({
  customerId,
  customerName,
  onClose,
}: CreateOpportunityForCustomerModalProps) {
  const navigate = useNavigate();
  const createOpportunity = useCreateOpportunity();
  const { data: typesData } = useRateCardTypes();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    servicesValue: '',
    currency: 'GBP' as 'GBP' | 'USD' | 'EUR',
    fundingTypeId: '',
    contractStartDate: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await createOpportunity.mutateAsync({
        customerId,
        title: formData.title,
        description: formData.description || undefined,
        servicesValue: formData.servicesValue ? parseFloat(formData.servicesValue) * 100 : undefined,
        currency: formData.currency,
        fundingTypeId: formData.fundingTypeId || undefined,
        contractStartDate: formData.contractStartDate || undefined,
      });

      onClose();
      navigate(`/opportunities/${result.opportunity.id}`);
    } catch (error) {
      console.error('Failed to create opportunity:', error);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">New Opportunity</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create a new opportunity for <span className="font-medium text-gray-700">{customerName}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Opportunity Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g., VCF Assessment Q2 2026"
              className="input mt-1 w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Opportunity description..."
              className="input mt-1 w-full"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Services Value</label>
              <input
                type="number"
                value={formData.servicesValue}
                onChange={(e) => setFormData({ ...formData, servicesValue: e.target.value })}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="input mt-1 w-full"
              />
            </div>
            <div>
              <label className="label">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value as 'GBP' | 'USD' | 'EUR' })
                }
                className="input mt-1 w-full"
              >
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Funding Type</label>
              <select
                value={formData.fundingTypeId}
                onChange={(e) => setFormData({ ...formData, fundingTypeId: e.target.value })}
                className="input mt-1 w-full"
              >
                <option value="">Select funding type...</option>
                {(typesData?.types || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Contract Start Date</label>
              <input
                type="date"
                value={formData.contractStartDate}
                onChange={(e) =>
                  setFormData({ ...formData, contractStartDate: e.target.value })
                }
                className="input mt-1 w-full"
              />
            </div>
          </div>
        </form>

        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.title || createOpportunity.isPending}
            className="btn-primary"
          >
            {createOpportunity.isPending ? 'Creating...' : 'Create Opportunity'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Create Project Modal ============

interface CreateProjectForCustomerModalProps {
  customerId: string;
  customerName: string;
  opportunities: Array<{
    id: string;
    title: string;
  }>;
  onClose: () => void;
}

function CreateProjectForCustomerModal({
  customerId: _customerId,
  customerName,
  opportunities,
  onClose,
}: CreateProjectForCustomerModalProps) {
  const navigate = useNavigate();
  const createProject = useCreateProjectUnderOpportunity();

  const [formData, setFormData] = useState({
    opportunityId: opportunities.length === 1 ? opportunities[0].id : '',
    name: '',
    description: '',
    deliveryLocation: 'Remote' as 'Remote' | 'Onsite' | 'Hybrid' | 'Manual',
    engagementStartDate: '',
    daysPerWeek: 5,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await createProject.mutateAsync({
        opportunityId: formData.opportunityId,
        name: formData.name,
        description: formData.description || undefined,
        deliveryLocation: formData.deliveryLocation,
        engagementStartDate: formData.engagementStartDate || undefined,
        daysPerWeek: formData.daysPerWeek,
      });

      onClose();
      navigate(`/projects/${result.project.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  if (opportunities.length === 0) {
    return (
      <div className="dialog-overlay">
        <div className="dialog max-w-lg">
          <div className="dialog-header">
            <h2 className="dialog-title">New Project</h2>
            <p className="text-sm text-gray-500 mt-1">
              For <span className="font-medium text-gray-700">{customerName}</span>
            </p>
          </div>

          <div className="dialog-body">
            <div className="text-center py-6">
              <FolderKanban className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-600 mb-1">
                No opportunities found for this customer.
              </p>
              <p className="text-sm text-gray-500">
                Create an opportunity first, then add projects to it.
              </p>
            </div>
          </div>

          <div className="dialog-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">New Project</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create a new project for <span className="font-medium text-gray-700">{customerName}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Opportunity *</label>
            <select
              value={formData.opportunityId}
              onChange={(e) => setFormData({ ...formData, opportunityId: e.target.value })}
              required
              className="input mt-1 w-full"
            >
              <option value="">Select an opportunity...</option>
              {opportunities.map((opp) => (
                <option key={opp.id} value={opp.id}>
                  {opp.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Project Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., VCF Assessment Phase"
              className="input mt-1 w-full"
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Project description..."
              className="input mt-1 w-full"
            />
          </div>

          <div>
            <label className="label">Delivery Location</label>
            <select
              value={formData.deliveryLocation}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  deliveryLocation: e.target.value as 'Remote' | 'Onsite' | 'Hybrid' | 'Manual',
                })
              }
              className="input mt-1 w-full"
            >
              <option value="Remote">Remote</option>
              <option value="Onsite">Onsite</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Manual">Manual</option>
            </select>
          </div>

          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={formData.engagementStartDate}
              onChange={(e) =>
                setFormData({ ...formData, engagementStartDate: e.target.value })
              }
              className="input mt-1 w-full"
            />
          </div>
        </form>

        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.opportunityId || !formData.name || createProject.isPending}
            className="btn-primary"
          >
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Helpers ============

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
